export interface SignedInOwner {
  id: string;
  email: string;
  fullName: string;
  displayName: string;
  timezone: string;
}

export interface SessionState {
  signedIn: boolean;
  owner?: SignedInOwner;
}

export type CalendarProvider = "microsoft" | "google" | "ics";

export type ConnectionStatus =
  "connected" | "needs_admin_consent" | "blocked_by_policy" | "expired" | "revoked";

export type ReadDepth = "free_busy_only" | "titles_too";

export interface ConnectedCalendar {
  id: string;
  name: string;
  isDefault: boolean;
  selected: boolean;
  colorIndex: number;
}

export interface Connection {
  id: string;
  provider: CalendarProvider;
  organization: string;
  accountEmail: string;
  status: ConnectionStatus;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  calendars: ConnectedCalendar[];
}

export interface ConnectionsResponse {
  connections: Connection[];
  readDepth: ReadDepth;
  microsoft: { clientId: string; redirectUri: string };
}

async function expectOk(response: Response): Promise<void> {
  if (!response.ok) {
    throw new Error(`Request failed with ${String(response.status)}`);
  }
}

export async function fetchSession(): Promise<SessionState> {
  const response = await fetch("/api/me", { credentials: "same-origin" });
  if (response.status === 401) return { signedIn: false };
  await expectOk(response);
  return (await response.json()) as SessionState;
}

export async function signOut(): Promise<void> {
  await expectOk(
    await fetch("/api/sign-out", { method: "POST", credentials: "same-origin" }),
  );
}

export async function fetchConnections(): Promise<ConnectionsResponse> {
  const response = await fetch("/api/connections", { credentials: "same-origin" });
  await expectOk(response);
  return (await response.json()) as ConnectionsResponse;
}

export async function setCalendarSelected(id: string, selected: boolean): Promise<void> {
  await expectOk(
    await fetch(`/api/calendars/${encodeURIComponent(id)}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ selected }),
    }),
  );
}

export async function renameOrganization(
  id: string,
  organization: string,
): Promise<void> {
  await expectOk(
    await fetch(`/api/connections/${encodeURIComponent(id)}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ organization }),
    }),
  );
}

export async function disconnectAccount(id: string): Promise<void> {
  await expectOk(
    await fetch(`/api/connections/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "same-origin",
    }),
  );
}

export async function setReadDepth(readDepth: ReadDepth): Promise<void> {
  await expectOk(
    await fetch("/api/owner/read-depth", {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ readDepth }),
    }),
  );
}

/**
 * Starts a calendar connection. The server returns the provider URL and the
 * browser navigates to it, which behaves identically behind a dev proxy and in
 * production, where a redirect from an API path does not.
 */
export async function beginConnect(provider: CalendarProvider): Promise<void> {
  const response = await fetch(`/api/connect/${provider}`, {
    credentials: "same-origin",
  });
  await expectOk(response);
  const { url } = (await response.json()) as { url: string };
  window.location.assign(url);
}

export interface IcsProblem {
  error: string;
  message?: string;
}

/**
 * Adds a published calendar link. Returns the publisher's message on failure so
 * the owner sees why - a typo here otherwise shows up much later as empty
 * availability.
 */
export async function addCalendarLink(
  url: string,
  label: string,
): Promise<{ eventCount: number }> {
  const response = await fetch("/api/connect/ics", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ url, label }),
  });
  if (!response.ok) {
    const problem = (await response
      .json()
      .catch(() => ({ error: "failed" }))) as IcsProblem;
    throw new Error(problem.message ?? "We couldn't add that link.");
  }
  return (await response.json()) as { eventCount: number };
}

export interface WorkingHourRange {
  id?: string;
  weekday: number;
  startMinutes: number;
  endMinutes: number;
}

export interface Blackout {
  id: string;
  label: string;
  kind: "once" | "weekly" | "yearly";
  allDay: boolean;
  startsAt: string;
  endsAt: string;
  weekdays: number[];
  repeatUntil: string | null;
}

export interface BookingSettings {
  durations: number[];
  minNoticeHours: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  windowWeeks: number;
}

export interface SettingsResponse {
  owner: {
    displayName: string;
    fullName: string;
    email: string;
    timezone: string;
    readDepth: ReadDepth;
  };
  bookingSettings: BookingSettings | null;
  workingHours: WorkingHourRange[];
  blackouts: Blackout[];
}

export async function fetchSettings(): Promise<SettingsResponse> {
  const response = await fetch("/api/settings", { credentials: "same-origin" });
  await expectOk(response);
  return (await response.json()) as SettingsResponse;
}

export async function saveSettings(patch: Record<string, unknown>): Promise<void> {
  await expectOk(
    await fetch("/api/settings", {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    }),
  );
}

export async function saveWorkingHours(hours: WorkingHourRange[]): Promise<void> {
  const response = await fetch("/api/working-hours", {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(
      hours.map((hour) => ({
        weekday: hour.weekday,
        startMinutes: hour.startMinutes,
        endMinutes: hour.endMinutes,
      })),
    ),
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      problem.error === "overlapping_ranges"
        ? "Two ranges on the same day overlap."
        : "Those hours could not be saved.",
    );
  }
}

export async function addBlackout(blackout: {
  label: string;
  kind: "once" | "weekly" | "yearly";
  allDay: boolean;
  startsAt: string;
  endsAt: string;
  weekdays: number[];
}): Promise<void> {
  await expectOk(
    await fetch("/api/blackouts", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(blackout),
    }),
  );
}

export async function deleteBlackout(id: string): Promise<void> {
  await expectOk(
    await fetch(`/api/blackouts/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "same-origin",
    }),
  );
}

export interface AvailabilityResponse {
  timezone: string;
  durationMinutes: number;
  computedAt: string;
  slots: { startUtc: string; endUtc: string }[];
  holidays: { date: string; name: string }[];
}

export async function fetchAvailability(
  durationMinutes: number,
  days: number,
): Promise<AvailabilityResponse> {
  const response = await fetch(
    `/api/availability?duration=${String(durationMinutes)}&days=${String(days)}`,
    { credentials: "same-origin" },
  );
  await expectOk(response);
  return (await response.json()) as AvailabilityResponse;
}

export interface RefreshResult {
  refreshedAt: string;
  results: { id: string; organization: string; events?: number; error?: string }[];
}

export async function refreshCalendars(): Promise<RefreshResult> {
  const response = await fetch("/api/calendars/refresh", {
    method: "POST",
    credentials: "same-origin",
  });
  await expectOk(response);
  return (await response.json()) as RefreshResult;
}

/* --- owner: requests ------------------------------------------------------ */

export type RequestStatus =
  | "requested"
  | "suggested"
  | "confirmed"
  | "cancellation_requested"
  | "declined"
  | "expired"
  | "withdrawn"
  | "cancelled";

export interface MovableClash {
  title: string | null;
  organization: string;
  startUtc: string;
  endUtc: string;
  /** Times this meeting could move to instead (1z). Empty means none found. */
  couldMoveTo: { startUtc: string; endUtc: string }[];
}

export interface OwnerRequest {
  id: string;
  status: RequestStatus;
  startUtc: string;
  endUtc: string;
  durationMinutes: number;
  purpose: string;
  flexibilityNote: string | null;
  visitorTimezone: string;
  requestedAt: string;
  expiresAt: string;
  visitor: { email: string; name: string | null };
  suggestions: { startUtc: string; endUtc: string; holdUntil: string }[];
  movableClashes: MovableClash[];
  fits: boolean;
}

export async function fetchRequests(): Promise<{ requests: OwnerRequest[] }> {
  const response = await fetch("/api/requests", { credentials: "same-origin" });
  await expectOk(response);
  return (await response.json()) as { requests: OwnerRequest[] };
}

async function act(id: string, action: string, body?: unknown): Promise<void> {
  const response = await fetch(`/api/requests/${encodeURIComponent(id)}/${action}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      problem.error === "not_allowed_from"
        ? "That request has already been answered."
        : "That didn't work.",
    );
  }
}

export const acceptRequest = (id: string) => act(id, "accept");
export const cancelMeeting = (id: string) => act(id, "cancel");
export const keepMeeting = (id: string) => act(id, "keep");
export const declineRequest = (id: string) => act(id, "decline");
export const suggestTimes = (id: string, slots: string[]) =>
  act(id, "suggest", { slots: slots.map((startUtc) => ({ startUtc })) });

/* --- visitor -------------------------------------------------------------- */

export interface VisitorSession {
  signedIn: boolean;
  visitor?: {
    email: string;
    name: string | null;
    ownerName: string;
    ownerTimezone: string;
  };
}

export async function fetchVisitorSession(): Promise<VisitorSession> {
  const response = await fetch("/api/visitor/me");
  if (response.status === 401) return { signedIn: false };
  await expectOk(response);
  return (await response.json()) as VisitorSession;
}

export async function requestVisitorCode(token: string): Promise<void> {
  await fetch("/api/visitor/code", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });
}

export async function verifyVisitorCode(
  token: string,
  code: string,
  name?: string,
): Promise<{ ownerName: string }> {
  const response = await fetch("/api/visitor/verify", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token, code, ...(name ? { name } : {}) }),
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      problem.error === "expired_code"
        ? "That code has expired. Ask for a new one."
        : problem.error === "too_many_attempts"
          ? "Too many attempts. Ask for a new code."
          : "That code wasn't right.",
    );
  }
  return (await response.json()) as { ownerName: string };
}

export interface VisitorAvailability {
  ownerName: string;
  ownerTimezone: string;
  durations: number[];
  durationMinutes: number;
  windowEnd: string;
  computedAt: string;
  /** When the oldest calendar behind these times was last read. */
  readAt: string | null;
  slots: { startUtc: string; endUtc: string }[];
}

export async function fetchVisitorAvailability(
  duration: number,
  days: number,
): Promise<VisitorAvailability> {
  const response = await fetch(
    `/api/visitor/availability?duration=${String(duration)}&days=${String(days)}`,
  );
  await expectOk(response);
  return (await response.json()) as VisitorAvailability;
}

export interface VisitorRequest {
  id: string;
  status: RequestStatus;
  startUtc: string;
  endUtc: string;
  durationMinutes: number;
  purpose: string;
  flexibilityNote: string | null;
  expiresAt: string;
  suggestions: { startUtc: string; endUtc: string; holdUntil: string }[];
}

export async function fetchVisitorRequests(): Promise<{ requests: VisitorRequest[] }> {
  const response = await fetch("/api/visitor/requests");
  await expectOk(response);
  return (await response.json()) as { requests: VisitorRequest[] };
}

/**
 * The one failure a visitor is likely to meet (2w): the instant they picked
 * filled while they were typing. Its own type because the screen says
 * something quite different for it than for a request that simply failed.
 */
export class SlotTaken extends Error {}

export async function submitRequest(input: {
  startUtc: string;
  durationMinutes: number;
  purpose: string;
  flexibilityNote?: string;
  visitorTimezone: string;
  name?: string;
}): Promise<void> {
  const response = await fetch("/api/visitor/requests", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { error?: string };
    if (problem.error === "slot_taken") {
      // Never says why. A meeting appearing, somebody else asking first and a
      // blackout the owner added an hour ago are all the same sentence here,
      // for the same reason a busy slot is simply absent from the list.
      throw new SlotTaken("That time is no longer free.");
    }
    throw new Error("We couldn't send that request.");
  }
}

export async function withdrawRequest(id: string): Promise<void> {
  await expectOk(
    await fetch(`/api/visitor/requests/${encodeURIComponent(id)}/withdraw`, {
      method: "POST",
    }),
  );
}

/* --- owner: merged calendar ---------------------------------------------- */

export interface OwnerEvent {
  id: string;
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  title: string | null;
  calendarName: string;
  organization: string;
  colorIndex: number;
  providerEventId: string | null;
  seriesMasterId: string | null;
  movable: boolean;
  movableSeries: boolean;
}

export async function fetchOwnerEvents(
  from: string,
  to: string,
): Promise<{ events: OwnerEvent[] }> {
  const response = await fetch(
    `/api/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { credentials: "same-origin" },
  );
  await expectOk(response);
  return (await response.json()) as { events: OwnerEvent[] };
}

export async function setMovable(input: {
  providerEventId: string;
  seriesMasterId?: string | null;
  scope: "occurrence" | "series";
  movable: boolean;
}): Promise<void> {
  await expectOk(
    await fetch("/api/events/movable", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }),
  );
}

export async function acceptSuggestion(id: string, startUtc: string): Promise<void> {
  const response = await fetch(
    `/api/visitor/requests/${encodeURIComponent(id)}/accept-suggestion`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ startUtc }),
    },
  );
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      problem.error === "hold_expired"
        ? "Those suggestions have expired. Pick another time instead."
        : problem.error === "slot_taken"
          ? "That time was taken in the meantime."
          : "That didn't work.",
    );
  }
}

export async function requestCancellation(id: string, reason?: string): Promise<void> {
  await expectOk(
    await fetch(`/api/visitor/requests/${encodeURIComponent(id)}/request-cancellation`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  );
}

/* --- owner: invitations --------------------------------------------------- */

export interface Invitation {
  id: string;
  email: string;
  visitorName: string | null;
  note: string | null;
  status: "invited" | "confirmed" | "revoked";
  sentAt: string;
  confirmedAt: string | null;
  lastActiveAt: string | null;
}

export async function fetchInvitations(): Promise<{ invitations: Invitation[] }> {
  const response = await fetch("/api/invitations", { credentials: "same-origin" });
  await expectOk(response);
  return (await response.json()) as { invitations: Invitation[] };
}

export async function sendInvitation(
  email: string,
  note?: string,
): Promise<{ link: string; email: string }> {
  const response = await fetch("/api/invitations", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, ...(note ? { note } : {}) }),
  });
  if (!response.ok) {
    throw new Error("That does not look like an email address.");
  }
  return (await response.json()) as { link: string; email: string };
}

export async function revokeInvitation(id: string): Promise<void> {
  await expectOk(
    await fetch(`/api/invitations/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
      credentials: "same-origin",
    }),
  );
}

/* --- visibility requests --------------------------------------------------- */

/**
 * Public. Deliberately returns nothing useful: the answer is the same whether
 * or not the person named uses Meet With, so there is nothing here to read.
 */
export async function askForVisibility(input: {
  target: string;
  requesterEmail: string;
  note?: string | undefined;
}): Promise<void> {
  const response = await fetch("/api/visibility-requests", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      target: input.target,
      requesterEmail: input.requesterEmail,
      ...(input.note ? { note: input.note } : {}),
    }),
  });
  if (response.status === 400) {
    throw new Error("Check the address you entered.");
  }
  if (!response.ok) {
    throw new Error("That didn't go through. Try again in a moment.");
  }
}

export interface VisibilityAsk {
  id: string;
  requesterEmail: string;
  note: string | null;
  createdAt: string;
}

export async function fetchVisibilityRequests(): Promise<{ requests: VisibilityAsk[] }> {
  const response = await fetch("/api/visibility-requests", {
    credentials: "same-origin",
  });
  await expectOk(response);
  return (await response.json()) as { requests: VisibilityAsk[] };
}

export async function dismissVisibilityRequest(id: string): Promise<void> {
  await expectOk(
    await fetch(`/api/visibility-requests/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      credentials: "same-origin",
    }),
  );
}
